import { useEffect, useRef, useState, useContext, useCallback } from "react";
import { createPortal } from "react-dom";
import { getApiUrl, getAuthHeaders } from "../utils/api";
import Cropper from "react-easy-crop";
import { useNavigate, useLocation } from "react-router-dom";
import { convertFromRaw } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import BadgeIcon from "../components/BadgeIcon";
import Skeleton, { SkeletonText, SkeletonAvatar } from "../components/Skeleton";
import { FaThumbtack, FaPen, FaCog, FaDownload, FaTimes, FaCamera, FaUpload, FaTrash, FaEye } from "react-icons/fa";
import Modal from "../components/Modal";
import { userSchemas } from "../validation/schemas";
import { validateForm, sanitizeFormData } from "../validation/validate";
import EmptyState from "../components/EmptyState";
import PageWithRail from "../components/PageWithRail";
import { compressAvatar, compressCoverImage } from "../utils/imageCompression";
import { getCroppedImageBlob } from "../utils/imageCrop";
import { UserContext } from "../context/UserContext";
import { downloadPoemAsPDF, downloadMultiplePoemsAsPDF } from "../utils/pdfExport";
import { getAvatarUrl, DEFAULT_AVATAR_DATA_URL, FALLBACK_AVATAR_SVG } from "../utils/avatar";
import { getProfileCoverUrl, onProfileCoverError } from "../utils/cover";
import { usePaginatedTab } from "../hooks/usePaginatedTab";
import ProfileTabLoadMore from "../components/ProfileTabLoadMore";

function getPoemPlainText(poem, maxLen = 180) {
  try {
    let raw = poem?.content;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
        if (typeof raw === "string") raw = JSON.parse(raw);
      } catch {
        /* plain string content */
      }
    }
    let html = "";
    if (raw && raw.blocks && typeof raw.entityMap !== "undefined") {
      html = stateToHTML(convertFromRaw(raw));
    } else {
      html = poem?.content || "";
    }
    const div = document.createElement("div");
    div.innerHTML = html;
    const text = (div.textContent || div.innerText || "").trim();
    if (!text) return "";
    return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
  } catch {
    return "";
  }
}

function formatPoemDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser: setContextUser } = useContext(UserContext);

  const [user, setUser] = useState(null);
  const [pinnedPoemId, setPinnedPoemId] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    name: "",
    bio: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [photoMenu, setPhotoMenu] = useState(null);
  const [viewPhoto, setViewPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const initialTab = new URLSearchParams(location.search).get("tab");
  const [activeTab, setActiveTab] = useState(
    ["poems", "drafts", "followers", "following", "saved"].includes(initialTab)
      ? initialTab
      : "poems"
  );
  const [deleteDraftModal, setDeleteDraftModal] = useState({ open: false, poemId: null });
  const [deleteProfileModal, setDeleteProfileModal] = useState({ open: false });
  const [deleteOTPModal, setDeleteOTPModal] = useState({ open: false });
  const [deleteOTP, setDeleteOTP] = useState("");
  const [deleteOTPLoading, setDeleteOTPLoading] = useState(false);
  const [deleteOTPError, setDeleteOTPError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  // Show loader immediately on file selection (before saving)
  const [selectingAvatar, setSelectingAvatar] = useState(false);
  const [selectingCover, setSelectingCover] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState({
    avatar: 0,
    coverImage: 0,
  });
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);
  const [unblocking, setUnblocking] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [errorModal, setErrorModal] = useState({ open: false, message: "" });
  const [downloadPoemsModal, setDownloadPoemsModal] = useState({ open: false });
  const [selectedPoems, setSelectedPoems] = useState(new Set());
  const [isDownloadingPoems, setIsDownloadingPoems] = useState(false);
  const settingsMenuRef = useRef(null);
  const profileTabsRef = useRef(null);
  const usernameRef = useRef(null);
  const emailRef = useRef(null);
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const photoMenuRef = useRef(null);
  const loggedInUser = JSON.parse(localStorage.getItem("user"));

  const fetchProfilePoems = useCallback(async (page, limit) => {
    const res = await fetch(
      getApiUrl(`/api/user/profile/poems?page=${page}&limit=${limit}`),
      { credentials: "include", headers: getAuthHeaders() }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return { items: data.poems || [], pagination: data.pagination };
  }, []);

  const fetchDraftsPage = useCallback(async (page, limit) => {
    const res = await fetch(
      getApiUrl(`/api/poems/drafts?page=${page}&limit=${limit}`),
      { credentials: "include", headers: getAuthHeaders() }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return { items: data.poems || [], pagination: data.pagination };
  }, []);

  const fetchFollowersPage = useCallback(async (page, limit) => {
    const res = await fetch(
      getApiUrl(`/api/user/profile/followers?page=${page}&limit=${limit}`),
      { credentials: "include", headers: getAuthHeaders() }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return { items: data.users || [], pagination: data.pagination };
  }, []);

  const fetchFollowingPage = useCallback(async (page, limit) => {
    const res = await fetch(
      getApiUrl(`/api/user/profile/following?page=${page}&limit=${limit}`),
      { credentials: "include", headers: getAuthHeaders() }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return { items: data.users || [], pagination: data.pagination };
  }, []);

  const fetchSavedPage = useCallback(async (page, limit) => {
    const res = await fetch(
      getApiUrl(`/api/user/me/saved?page=${page}&limit=${limit}`),
      { credentials: "include", headers: getAuthHeaders() }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return { items: data.poems || [], pagination: data.pagination };
  }, []);

  const poemsFeed = usePaginatedTab({
    enabled: !!user && activeTab === "poems",
    resetKey: `${user?._id}-poems`,
    fetchPage: fetchProfilePoems,
  });
  const draftsFeed = usePaginatedTab({
    enabled: !!user && activeTab === "drafts",
    resetKey: `${user?._id}-drafts`,
    fetchPage: fetchDraftsPage,
  });
  const followersFeed = usePaginatedTab({
    enabled: !!user && activeTab === "followers",
    resetKey: `${user?._id}-followers`,
    fetchPage: fetchFollowersPage,
  });
  const followingFeed = usePaginatedTab({
    enabled: !!user && activeTab === "following",
    resetKey: `${user?._id}-following`,
    fetchPage: fetchFollowingPage,
  });
  const savedFeed = usePaginatedTab({
    enabled: !!user && activeTab === "saved",
    resetKey: `${user?._id}-saved`,
    fetchPage: fetchSavedPage,
  });

  const poems = poemsFeed.items;
  const drafts = draftsFeed.items;
  const savedPoems = savedFeed.items;

  const followUser = async (uname) => {
    try {
      const res = await fetch(getApiUrl(`/api/user/${uname}/follow`), {
        method: "PUT",
        credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      if (res.ok) {
        setUser((prev) => ({
          ...prev,
          following: [...(prev.following || []), { username: uname }],
        }));
      }
    } catch {}
  };

  const unfollowUser = async (uname) => {
    try {
      const res = await fetch(getApiUrl(`/api/user/${uname}/unfollow`), {
        method: "PUT",
        credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      if (res.ok) {
        setUser((prev) => ({
          ...prev,
          following: (prev.following || []).filter((u) => u.username !== uname),
        }));
      }
    } catch {}
  };

  // Cropping state
  const [isCropping, setIsCropping] = useState(false);
  const [cropType, setCropType] = useState(null); // 'avatar' | 'cover'
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropImageSrc, setCropImageSrc] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Add timeout to prevent hanging requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        const res = await fetch(
          getApiUrl("/api/user/profile"),
          {
            credentials: "include",
            headers: getAuthHeaders(),
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId);

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          if (res.status === 401 || res.status === 403) {
            const msg = String(errorData.msg || errorData.error || "").toLowerCase();
            const hasToken = !!localStorage.getItem("token");
            if (
              !hasToken ||
              msg.includes("jwt expired") ||
              msg.includes("invalid token") ||
              msg.includes("no token")
            ) {
              localStorage.removeItem("user");
              localStorage.removeItem("token");
              setContextUser(null);
              navigate("/login");
              return;
            }
          }
          throw new Error(errorData.error || errorData.msg || `Failed to fetch profile: ${res.status}`);
        }

        const data = await res.json();
        if (!data.user) {
          throw new Error("User data not found in response");
        }
        setUser(data.user);
        setPinnedPoemId(data.pinnedPoemId || null);
        setFormData({
          username: data.user.username || "",
          email: data.user.email,
          name: data.user.name || "",
          bio: data.user.bio || "",
        });
      } catch (err) {
        console.error("Error fetching profile:", err);
        if (err.name === "AbortError") {
          console.error("Profile fetch timed out");
        } else {
          console.error("Profile fetch failed:", err.message);
        }
        // Don't set user to null here, let the component handle the error state
      }
    };

    fetchProfile().finally(() => {
      setIsLoading(false);
    });
  }, []);

  // Auto-open edit modal for new Google OAuth users (no username)
  useEffect(() => {
    if (user && !user.username && !isLoading) {
      setIsEditing(true);
    }
  }, [user, isLoading]);

  // Load blocked users when settings modal opens
  useEffect(() => {
    if (!showSettingsModal) return;
    (async () => {
      try {
        setLoadingBlocked(true);
        const res = await fetch(getApiUrl("/api/user/blocked"), {
          credentials: "include",
          headers: getAuthHeaders(),
        });
        const data = await res.json().catch(() => []);
        if (res.ok) {
          const list = Array.isArray(data) ? data : Array.isArray(data.users) ? data.users : [];
          setBlockedUsers(list);
        }
      } catch (_) {}
      finally {
        setLoadingBlocked(false);
      }
    })();
  }, [showSettingsModal]);

  // Handle clicks outside the settings menu dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        settingsMenuRef.current &&
        !settingsMenuRef.current.contains(event.target)
      ) {
        setShowSettingsMenu(false);
      }
    };

    if (showSettingsMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showSettingsMenu]);

  useEffect(() => {
    if (!isEditing) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isEditing]);

  useEffect(() => {
    if (!photoMenu) return undefined;
    const handleClickOutside = (event) => {
      if (photoMenuRef.current && !photoMenuRef.current.contains(event.target)) {
        setPhotoMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [photoMenu]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 200);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const activeTabEl = profileTabsRef.current?.querySelector(
      ".peer-home-tab--active"
    );
    activeTabEl?.scrollIntoView({
      inline: "nearest",
      block: "nearest",
      behavior: "smooth",
    });
  }, [activeTab]);

  const handleUnblock = async (uname) => {
    if (unblocking) return;
    try {
      setUnblocking(true);
      const res = await fetch(getApiUrl(`/api/user/${uname}/block`), {
        method: "DELETE",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setBlockedUsers((prev) => prev.filter((u) => u.username !== uname));
        // Let other parts refresh if needed
        try {
          window.dispatchEvent(new CustomEvent("userUnblocked", { detail: { username: uname } }));
        } catch {}
      }
    } finally {
      setUnblocking(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "avatar") {
      const file = files[0];
      if (file) {
        // Validate file type on client side
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
          setErrorModal({
            open: true,
            message:
              "Invalid file type. Avatar must be an image file (JPEG, PNG, GIF, WebP).",
          });
          // Clear the file input
          e.target.value = "";
          return;
        }
        // Clear any previous errors
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.general;
          return newErrors;
        });
        setSelectingAvatar(true);
        // Use setTimeout to ensure state update is processed before opening cropper
        setTimeout(() => {
          openCropper(file, "avatar");
        }, 10);
      }
    } else if (name === "coverImage") {
      const file = files[0];
      if (file) {
        // Validate file type on client side
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
          setErrorModal({
            open: true,
            message:
              "Invalid file type. Cover image must be an image file (JPEG, PNG, GIF, WebP).",
          });
          // Clear the file input
          e.target.value = "";
          return;
        }
        // Clear any previous errors
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.general;
          return newErrors;
        });
        setSelectingCover(true);
        // Use setTimeout to ensure state update is processed before opening cropper
        setTimeout(() => {
          openCropper(file, "cover");
        }, 10);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      // Clear error for this field when user starts typing
      if (formErrors[name]) {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  const openCropper = (file, type) => {
    setCroppedAreaPixels(null);
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setIsCropping(true);
      setCropType(type);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      // stop selection loader once cropper opens
      if (type === "avatar") setSelectingAvatar(false);
      if (type === "cover") setSelectingCover(false);
    };
    reader.onerror = () => {
      if (type === "avatar") setSelectingAvatar(false);
      if (type === "cover") setSelectingCover(false);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const closeCropper = () => {
    setIsCropping(false);
    setCropType(null);
    setCropImageSrc(null);
    setCroppedAreaPixels(null);
    if (avatarInputRef.current) avatarInputRef.current.value = "";
    if (coverInputRef.current) coverInputRef.current.value = "";
  };

  const applyUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setContextUser(updatedUser);
  };

  const uploadProfileMedia = async (type, file) => {
    setPhotoMenu(null);
    setUploadingPhoto(type);
    setIsUploading(true);
    setUploadProgress({ avatar: 0, coverImage: 0 });
    const progressKey = type === "avatar" ? "avatar" : "coverImage";
    const form = new FormData();
    form.append(type === "avatar" ? "avatar" : "coverImage", file);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => ({
          ...prev,
          [progressKey]: Math.min(prev[progressKey] + 10, 90),
        }));
      }, 200);

      const res = await fetch(getApiUrl("/api/user/profile"), {
        method: "PUT",
        credentials: "include",
        headers: getAuthHeaders(),
        body: form,
      });

      clearInterval(progressInterval);
      setUploadProgress((prev) => ({ ...prev, [progressKey]: 100 }));

      let data;
      try {
        data = await res.json();
      } catch {
        throw new Error("Failed to update photo. Please try again.");
      }

      if (!res.ok) {
        throw new Error(
          data?.error || data?.msg || data?.message || "Failed to update photo."
        );
      }

      applyUserUpdate(data.user);
    } catch (err) {
      setErrorModal({
        open: true,
        message: err.message || "Could not update photo. Please try again.",
      });
    } finally {
      setTimeout(() => {
        setIsUploading(false);
        setUploadingPhoto(null);
        setUploadProgress({ avatar: 0, coverImage: 0 });
      }, 400);
    }
  };

  const removeProfilePhoto = async (type) => {
    setPhotoMenu(null);
    setUploadingPhoto(type);
    setIsUploading(true);

    const form = new FormData();
    form.append(type === "avatar" ? "removeAvatar" : "removeCoverImage", "true");

    try {
      const res = await fetch(getApiUrl("/api/user/profile"), {
        method: "PUT",
        credentials: "include",
        headers: getAuthHeaders(),
        body: form,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data?.error || data?.msg || data?.message || "Failed to remove photo."
        );
      }

      applyUserUpdate(data.user);
    } catch (err) {
      setErrorModal({
        open: true,
        message: err.message || "Could not remove photo. Please try again.",
      });
    } finally {
      setIsUploading(false);
      setUploadingPhoto(null);
    }
  };

  const confirmCrop = async () => {
    if (!cropImageSrc || !cropType) {
      closeCropper();
      return;
    }
    if (!croppedAreaPixels) {
      setErrorModal({
        open: true,
        message:
          "Please wait for the image to load, then adjust the crop and try again.",
      });
      return;
    }
    try {
      const blob = await getCroppedImageBlob(cropImageSrc, croppedAreaPixels);
      const fileName = `${cropType}_cropped_${Date.now()}.jpg`;
      const croppedFile = new File([blob], fileName, { type: "image/jpeg" });

      let compressedFile;
      try {
        if (cropType === "avatar") {
          compressedFile = await compressAvatar(croppedFile);
        } else {
          compressedFile = await compressCoverImage(croppedFile);
        }
      } catch (compressionError) {
        console.warn("Compression failed, using original:", compressionError);
        compressedFile = croppedFile;
      }

      closeCropper();
      await uploadProfileMedia(cropType, compressedFile);
    } catch (err) {
      console.error("Error processing image:", err);
      setErrorModal({
        open: true,
        message: "Could not process that image. Please try another photo.",
      });
      closeCropper();
    }
  };

  const handleUpdate = async () => {
    // Clear previous errors
    setFormErrors({});

    // For Google OAuth users without a username, username is required
    if (!user.username && !formData.username.trim()) {
      setFormErrors({ username: "Pen name is required. Please choose a pen name." });
      return;
    }

    // Validate form data (excluding files)
    const validationData = {
      username: formData.username,
      email: formData.email,
      name: formData.name,
      bio: formData.bio,
    };

    const validation = await validateForm(
      userSchemas.updateProfile,
      validationData
    );
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    // Sanitize form data
    const sanitizedData = sanitizeFormData(validation.data);

    setIsUploading(true);

    const form = new FormData();
    form.append("username", sanitizedData.username);
    form.append("email", sanitizedData.email);
    form.append("name", formData.name);
    form.append("bio", sanitizedData.bio);

    try {
      const res = await fetch(getApiUrl("/api/user/profile"), {
        method: "PUT",
        credentials: "include",
        headers: getAuthHeaders(),
        body: form,
      });

      let data;
      try {
        data = await res.json();
      } catch (jsonError) {
        // If response is not valid JSON, show a generic error
        const errorMessage = res.status === 400 
          ? "Invalid file type. Please upload an image file (JPEG, PNG, GIF, WebP)."
          : "Failed to update profile. Please try again.";
        setFormErrors({ general: errorMessage });
        setIsUploading(false);
        return;
      }

      if (!res.ok) {
        const message = String(data?.error || data?.message || data?.msg || "Update failed");
        const isDuplicateUsername =
          /username\s*(is)?\s*already\s*taken|duplicate\s*key.*username/i.test(
            message
          );
        const isDuplicateEmail =
          /account\s*with\s*this\s*email\s*already\s*exists|duplicate\s*key.*email/i.test(
            message
          );
        const isInvalidFileType =
          /invalid\s*file\s*type|must\s*be\s*an\s*image/i.test(message);
        
        if (isDuplicateUsername) {
          setFormErrors({ username: "Pen name is already taken. Please choose another one." });
          // Surface error near the input
          setTimeout(() => {
            try {
              usernameRef.current && usernameRef.current.focus();
            } catch {}
          }, 0);
        } else if (isDuplicateEmail) {
          setFormErrors({ email: "An account with this email already exists. Please use another email." });
          // Surface error near the input
          setTimeout(() => {
            try {
              emailRef.current && emailRef.current.focus();
            } catch {}
          }, 0);
        } else if (isInvalidFileType) {
          setFormErrors({ general: message });
        } else {
          // Fallback: show generic error
          setFormErrors({ general: message });
        }
        setIsUploading(false);
        return;
      }

      setUser(data.user);
      localStorage.setItem("user", JSON.stringify(data.user));
      setContextUser(data.user);
      setIsEditing(false);
      setFormErrors({});

      setIsUploading(false);
    } catch (err) {
      console.error("Error updating profile:", err);
      setFormErrors({ general: err.message || "Failed to update profile. Please try again." });
      setIsUploading(false);
    }
  };

  const handleGenerateDeleteOTP = async () => {
    // Open OTP modal immediately with loader
    setDeleteProfileModal({ open: false });
    setDeleteOTPModal({ open: true });
    setDeleteOTP(""); // Clear OTP input to show loader
    setDeleteOTPError(""); // Clear any previous errors
    setIsEditing(false); // Close edit modal when opening OTP modal
    setDeleteOTPLoading(true); // Show loader immediately
    
    try {
      const res = await fetch(getApiUrl("/api/user/profile/generate-delete-otp"), {
        method: "POST",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}` 
        },
      });

      const data = await res.json();
      if (res.ok) {
        // Hide loader after a short delay to ensure email is sent
        setTimeout(() => {
          setDeleteOTPLoading(false);
        }, 2000); // Show loader for 2 seconds to ensure email is sent
      } else {
        setDeleteOTPError(data.error || data.msg || "Failed to generate OTP");
        setDeleteOTPLoading(false);
      }
    } catch (err) {
      console.error("Error generating delete OTP:", err);
      setDeleteOTPError("Failed to generate OTP. Please try again.");
      setDeleteOTPLoading(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!deleteOTP.trim()) {
      setDeleteOTPError("Please enter the OTP");
      return;
    }

    try {
      setDeleteOTPLoading(true);
      setDeleteOTPError("");

      const res = await fetch(getApiUrl("/api/user/profile"), {
        method: "DELETE",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}` 
        },
        body: JSON.stringify({ otp: deleteOTP }),
      });

      if (res.ok) {
        // Clear local storage
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("cookieConsent");
        localStorage.removeItem("cookieConsentDate");
        localStorage.removeItem("cookieConsentUserId");
        
        // Close modals
        setDeleteOTPModal({ open: false });
        setDeleteProfileModal({ open: false });
        setIsEditing(false);
        
        // Redirect to login
        navigate("/login");
      } else {
        const data = await res.json();
        setDeleteOTPError(data.error || data.msg || "Failed to delete profile");
      }
    } catch (err) {
      console.error("Error deleting profile:", err);
      setDeleteOTPError("Failed to delete profile. Please try again.");
    } finally {
      setDeleteOTPLoading(false);
    }
  };

  if (isLoading) {
    return (
      <PageWithRail title="Profile" showRail={false}>
      <div className="w-full">
        <div className="profile-hero">
          <Skeleton className="profile-hero__cover rounded-none" />
          <div className="profile-hero__body">
            <div className="profile-hero__top">
              <Skeleton className="h-[6.5rem] w-[6.5rem] sm:h-[7.5rem] sm:w-[7.5rem] rounded-full border-4 border-white" />
              <div className="flex gap-2 pb-1">
                <Skeleton className="h-10 w-28 rounded-lg" />
                <Skeleton className="h-10 w-10 rounded-lg" />
              </div>
            </div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-32 mb-4" />
            <SkeletonText lines={2} className="mb-4 max-w-xl" />
            <div className="flex gap-3 mt-4 pt-4 border-t border-[#eeeeee]">
              <Skeleton className="h-14 w-24 rounded-xl" />
              <Skeleton className="h-14 w-24 rounded-xl" />
              <Skeleton className="h-14 w-24 rounded-xl" />
            </div>
          </div>
        </div>
        <div className="flex gap-6 border-b mb-6 max-w-[52rem] mx-auto">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-24" />
        </div>
        <div className="profile-panel space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="profile-poem-card pointer-events-none">
              <Skeleton className="h-3 w-16 mb-3" />
              <Skeleton className="h-6 w-1/2 mb-2" />
              <SkeletonText lines={2} />
            </div>
          ))}
        </div>
      </div>
      </PageWithRail>
    );
  }

  if (!user) {
    return (
      <PageWithRail title="Profile" showRail={false}>
      <div className="w-full">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-gray-500 text-lg mb-4">Profile not found</div>
            <p className="text-gray-600 mb-4">
              Unable to load your profile. This could be due to:
            </p>
            <ul className="text-sm text-gray-500 text-left max-w-md mx-auto mb-6">
              <li>• You're not logged in</li>
              <li>• Your session has expired</li>
              <li>• Network connection issues</li>
            </ul>
            <button
              onClick={() => window.location.reload()}
              className="peer-home-btn-primary px-4 py-2 mr-2"
            >
              Retry
            </button>
            <button
              onClick={() => navigate("/login")}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
      </PageWithRail>
    );
  }

  const getAvatar = (u) => getAvatarUrl(u?.avatar);

  const getCoverImage = (user) => getProfileCoverUrl(user?.coverImage);

  const hasCustomAvatar = (u) =>
    Boolean(
      u?.avatar &&
        typeof u.avatar === "string" &&
        u.avatar.trim() &&
        !u.avatar.startsWith("http")
    );

  const hasCustomCover = (u) =>
    Boolean(u?.coverImage && typeof u.coverImage === "string" && u.coverImage.trim());

  // Helper for joined date
  const joinedDate = user.createdAt
    ? new Date(user.createdAt).getFullYear()
    : "";

  return (
    <PageWithRail title={user?.username ? `${user.username} – Profile` : "Profile"} showRail={false}>
    <div className="w-full min-w-0">
      <Modal
        open={deleteDraftModal.open}
        title="Delete Draft?"
        message="Are you sure you want to delete this draft? This action cannot be undone."
        type="error"
        onClose={() => setDeleteDraftModal({ open: false, poemId: null })}
        onConfirm={async () => {
          try {
            const res = await fetch(
              getApiUrl(`/api/poems/${deleteDraftModal.poemId}`),
              { method: "DELETE", credentials: "include" }
            );
            if (res.ok) {
              draftsFeed.setItems((prev) =>
                prev.filter((p) => p._id !== deleteDraftModal.poemId)
              );
            }
          } finally {
            setDeleteDraftModal({ open: false, poemId: null });
          }
        }}
        confirmText="Delete"
      />
      <Modal
        open={errorModal.open}
        title="Error"
        message={errorModal.message}
        type="error"
        onClose={() => setErrorModal({ open: false, message: "" })}
      />
      <Modal
        open={deleteProfileModal.open}
        title="Delete Profile?"
        message="Are you sure you want to delete your profile? This action cannot be undone. All your poems, followers, and data will be permanently deleted. You will receive an OTP via email to confirm."
        type="error"
        onClose={() => setDeleteProfileModal({ open: false })}
        onConfirm={handleGenerateDeleteOTP}
        confirmText="Continue"
        confirmLoading={deleteOTPLoading}
      />
      {/* OTP Modal for Profile Deletion */}
      {deleteOTPModal.open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="peer-home-page-card w-full max-w-md p-6">
            {deleteOTPLoading && deleteOTP === "" ? (
              // Show loader while generating OTP
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f26522] mx-auto mb-4"></div>
                <h3 className="text-xl font-semibold mb-2">Generating OTP</h3>
                <p className="text-sm text-gray-600">
                  Please wait while we send a verification code to your email...
                </p>
              </div>
            ) : (
              // Show OTP input form
              <>
                <h3 className="text-xl font-semibold mb-2">Enter OTP</h3>
                <p className="text-sm text-gray-600 mb-4">
                  We've sent a verification code to your email. Please enter it below to delete your profile.
                </p>
                {deleteOTPError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                    {deleteOTPError}
                  </div>
                )}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={deleteOTP}
                    onChange={(e) => {
                      setDeleteOTP(e.target.value);
                      setDeleteOTPError("");
                    }}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="w-full px-4 py-2 border border-[#eeeeee] rounded-lg focus:border-[#f26522] focus:outline-none text-center text-lg tracking-widest"
                    disabled={deleteOTPLoading}
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setDeleteOTPModal({ open: false });
                      setDeleteOTP("");
                      setDeleteOTPError("");
                    }}
                    disabled={deleteOTPLoading}
                    className="flex-1 px-4 py-2 border border-[#eeeeee] rounded-lg text-gray-700 hover:bg-[#fff5ef] disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteProfile}
                    disabled={deleteOTPLoading || !deleteOTP.trim()}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {deleteOTPLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Deleting...
                      </>
                    ) : (
                      "Delete Profile"
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Download Poems Modal */}
      {downloadPoemsModal.open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="peer-home-page-card w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">Download Poems as PDF</h3>
              <button
                onClick={() => {
                  setDownloadPoemsModal({ open: false });
                  setSelectedPoems(new Set());
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="px-6 py-4 border-b border-gray-200">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={poems.length > 0 && selectedPoems.size === poems.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedPoems(new Set(poems.map(p => p._id)));
                    } else {
                      setSelectedPoems(new Set());
                    }
                  }}
                  className="w-5 h-5 text-[#f26522] border-[#eeeeee] rounded focus:ring-[#f26522]"
                />
                <div className="flex-1 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-800 group-hover:text-[#f26522]">
                    {selectedPoems.size === poems.length ? "Deselect All" : "Select All"}
                  </span>
                  <span className="text-sm text-gray-600">
                    {selectedPoems.size} of {poems.length} selected
                  </span>
                </div>
              </label>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {poems.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No poems available</p>
              ) : (
                <div className="space-y-3">
                  {poems.map((poem) => (
                    <label
                      key={poem._id}
                      className="flex items-start gap-3 p-3 rounded-lg border border-[#eeeeee] hover:border-[#f26522] hover:bg-[#fff5ef] cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPoems.has(poem._id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedPoems);
                          if (e.target.checked) {
                            newSelected.add(poem._id);
                          } else {
                            newSelected.delete(poem._id);
                          }
                          setSelectedPoems(newSelected);
                        }}
                        className="mt-1 w-4 h-4 text-[#f26522] border-[#eeeeee] rounded focus:ring-[#f26522]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-800 mb-1">{poem.title}</div>
                        <div className="text-sm text-gray-500">
                          {poem.createdAt
                            ? new Date(poem.createdAt).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : "No date"}
                        </div>
                        {poem.tags && poem.tags.length > 0 && (
                          <div className="text-xs text-gray-400 mt-1">
                            Tags: {poem.tags.slice(0, 3).join(", ")}
                            {poem.tags.length > 3 && ` +${poem.tags.length - 3} more`}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setDownloadPoemsModal({ open: false });
                  setSelectedPoems(new Set());
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-[#eeeeee] rounded-lg hover:bg-[#fff5ef]"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (selectedPoems.size === 0) {
                    setErrorModal({
                      open: true,
                      message: "Please select at least one poem to download.",
                    });
                    return;
                  }
                  setIsDownloadingPoems(true);
                  try {
                    const selectedPoemsArray = poems.filter((p) => selectedPoems.has(p._id));
                    downloadMultiplePoemsAsPDF(selectedPoemsArray, user);
                    setDownloadPoemsModal({ open: false });
                    setSelectedPoems(new Set());
                  } catch (error) {
                    setErrorModal({
                      open: true,
                      message: error.message || "Failed to download PDF. Please try again.",
                    });
                  } finally {
                    setIsDownloadingPoems(false);
                  }
                }}
                disabled={selectedPoems.size === 0 || isDownloadingPoems}
                className="px-4 py-2 text-sm font-medium text-white bg-[#f26522] rounded-lg hover:bg-[#d95518] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDownloadingPoems ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <FaDownload className="w-4 h-4" />
                    Download Selected ({selectedPoems.size})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {isCropping &&
        createPortal(
          <div
            className="profile-crop-modal fixed inset-0 z-[10050] bg-black/60 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-label={cropType === "avatar" ? "Crop profile picture" : "Crop cover image"}
          >
            <div className="peer-home-page-card p-4 w-full max-w-3xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {cropType === "avatar" ? "Crop profile picture" : "Crop cover image"}
              </h3>
              <div className="relative w-full h-[50vh] sm:h-[60vh] bg-black rounded-lg overflow-hidden">
                <Cropper
                  image={cropImageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={cropType === "avatar" ? 1 : 16 / 9}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  cropShape={cropType === "avatar" ? "round" : "rect"}
                  showGrid={false}
                />
              </div>
              <div className="mt-4 flex items-center gap-4">
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="flex-1"
                  aria-label="Zoom"
                />
                <span className="text-sm text-gray-600 shrink-0">Zoom</span>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm"
                  onClick={closeCropper}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="peer-home-btn-primary px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={confirmCrop}
                  disabled={!croppedAreaPixels}
                >
                  Use image
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      {photoMenu &&
        createPortal(
          <div className="profile-photo-menu" role="dialog" aria-modal="true">
            <div
              className="profile-photo-menu__backdrop"
              aria-hidden="true"
              onClick={() => setPhotoMenu(null)}
            />
            <div className="profile-photo-menu__card" ref={photoMenuRef}>
              <div className="profile-photo-menu__header">
                <h3>
                  {photoMenu === "avatar"
                    ? "Edit Profile Photo"
                    : "Edit Cover Photo"}
                </h3>
                <button
                  type="button"
                  className="profile-photo-menu__close"
                  aria-label="Close"
                  onClick={() => setPhotoMenu(null)}
                >
                  <FaTimes aria-hidden="true" />
                </button>
              </div>
              <button
                type="button"
                className="profile-photo-menu__action"
                onClick={() => {
                  setPhotoMenu(null);
                  if (photoMenu === "avatar") {
                    avatarInputRef.current?.click();
                  } else {
                    coverInputRef.current?.click();
                  }
                }}
              >
                <FaUpload aria-hidden="true" />
                Upload New Photo
              </button>
              <button
                type="button"
                className="profile-photo-menu__action profile-photo-menu__action--danger"
                disabled={
                  photoMenu === "avatar"
                    ? !hasCustomAvatar(user)
                    : !hasCustomCover(user)
                }
                onClick={() => removeProfilePhoto(photoMenu)}
              >
                <FaTrash aria-hidden="true" />
                Remove Photo
              </button>
              <button
                type="button"
                className="profile-photo-menu__action"
                onClick={() => {
                  const url =
                    photoMenu === "avatar"
                      ? getAvatar(user)
                      : getCoverImage(user);
                  setPhotoMenu(null);
                  setViewPhoto({
                    url,
                    title:
                      photoMenu === "avatar"
                        ? "Profile picture"
                        : "Cover photo",
                  });
                }}
              >
                <FaEye aria-hidden="true" />
                View Photo
              </button>
            </div>
          </div>,
          document.body
        )}
      {viewPhoto &&
        createPortal(
          <div
            className="profile-photo-view"
            role="dialog"
            aria-modal="true"
            aria-label={viewPhoto.title}
          >
            <div
              className="profile-photo-view__backdrop"
              onClick={() => setViewPhoto(null)}
              aria-hidden="true"
            />
            <div className="profile-photo-view__card">
              <div className="profile-photo-view__header">
                <h3>{viewPhoto.title}</h3>
                <button
                  type="button"
                  className="profile-photo-view__close"
                  aria-label="Close"
                  onClick={() => setViewPhoto(null)}
                >
                  <FaTimes aria-hidden="true" />
                </button>
              </div>
              <img
                src={viewPhoto.url}
                alt={viewPhoto.title}
                className={`profile-photo-view__img${
                  viewPhoto.title === "Profile picture"
                    ? " profile-photo-view__img--avatar"
                    : " profile-photo-view__img--cover"
                }`}
              />
            </div>
          </div>,
          document.body
        )}
      {/* Profile header */}
      <section className="profile-hero" aria-label="Your profile">
        <div className="profile-hero__cover">
          <img
            src={getCoverImage(user)}
            alt=""
            className="profile-hero__cover-img"
            onError={onProfileCoverError}
          />
          <div className="profile-hero__cover-shade" aria-hidden="true" />
          <input
            ref={coverInputRef}
            type="file"
            name="coverImage"
            accept="image/*"
            onChange={handleChange}
            className="profile-header-photo-input"
            tabIndex={-1}
            aria-hidden="true"
          />
          <button
            type="button"
            className="profile-header-photo-btn profile-header-photo-btn--cover"
            onClick={() => setPhotoMenu("cover")}
            disabled={isUploading}
            aria-label="Edit cover photo"
          >
            <FaCamera aria-hidden="true" />
          </button>
          {(uploadingPhoto === "cover" || selectingCover) && (
            <div className="absolute inset-0 bg-black/55 flex items-center justify-center backdrop-blur-[2px] z-[3]">
              <div className="text-center px-4">
                <div className="animate-spin rounded-full h-9 w-9 border-2 border-white border-t-transparent mx-auto mb-3" />
                <div className="text-white text-sm font-medium">
                  {selectingCover ? "Loading cover..." : "Uploading cover..."}
                </div>
                <div className="w-36 bg-white/20 rounded-full h-1.5 mt-3 mx-auto overflow-hidden">
                  <div
                    className="bg-[#f26522] h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress.coverImage}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="profile-hero__body">
          <div className="profile-hero__top">
            <div className="profile-hero__avatar-wrap">
              <img
                src={getAvatar(user)}
                alt=""
                className="profile-hero__avatar"
                onError={(e) => {
                  e.currentTarget.src =
                    e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                      ? FALLBACK_AVATAR_SVG
                      : DEFAULT_AVATAR_DATA_URL;
                }}
              />
              <input
                ref={avatarInputRef}
                type="file"
                name="avatar"
                accept="image/*"
                onChange={handleChange}
                className="profile-header-photo-input"
                tabIndex={-1}
                aria-hidden="true"
              />
              <button
                type="button"
                className="profile-header-photo-btn profile-header-photo-btn--avatar"
                onClick={() => setPhotoMenu("avatar")}
                disabled={isUploading}
                aria-label="Edit profile picture"
              >
                <FaCamera aria-hidden="true" />
              </button>
              {(uploadingPhoto === "avatar" || selectingAvatar) && (
                <div className="absolute inset-0 bg-black/55 rounded-full flex items-center justify-center backdrop-blur-[2px] z-[3]">
                  <div className="text-center px-2">
                    <div className="animate-spin rounded-full h-7 w-7 border-2 border-white border-t-transparent mx-auto mb-1.5" />
                    <div className="text-white text-[10px] font-medium leading-tight">
                      {selectingAvatar ? "Loading..." : "Uploading..."}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="profile-hero__actions">
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="peer-home-btn-primary"
              >
                <FaPen className="w-3.5 h-3.5 mr-2" aria-hidden="true" />
                Edit profile
              </button>
              <div className="relative" ref={settingsMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                  className="profile-hero__btn-secondary profile-hero__btn-icon"
                  aria-label="Settings"
                  title="Settings"
                >
                  <FaCog className="w-4 h-4" />
                </button>
                {showSettingsMenu && (
                  <div className="absolute right-0 mt-2 w-56 peer-home-page-card border border-[#eeeeee] z-50 py-1 shadow-lg">
                    <button
                      type="button"
                      onClick={() => {
                        setShowSettingsMenu(false);
                        setShowSettingsModal(true);
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-[#fff5ef] transition-colors flex items-center gap-2"
                    >
                      <span>Blocked users</span>
                      {blockedUsers.length > 0 && (
                        <span className="ml-auto text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          {blockedUsers.length}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowSettingsMenu(false);
                        setDeleteProfileModal({ open: true });
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Delete profile
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="profile-hero__identity">
            {user.name ? (
              <h1 className="profile-hero__name">{user.name}</h1>
            ) : (
              <h1 className="profile-hero__name">
                {user.username ? `@${user.username}` : "Your profile"}
              </h1>
            )}
            <div className="profile-hero__handle">
              {user.username ? (
                <span>@{user.username}</span>
              ) : (
                <span className="profile-hero__handle--empty">No pen name set yet</span>
              )}
              <BadgeIcon type={user.badge} />
            </div>
            {user.bio && <p className="profile-hero__bio">{user.bio}</p>}
            {joinedDate && (
              <div className="profile-hero__meta">
                <span>Joined {joinedDate}</span>
              </div>
            )}
          </div>

          <div className="profile-hero__stats" role="group" aria-label="Profile stats">
            <button
              type="button"
              className={`profile-hero__stat${activeTab === "poems" ? " profile-hero__stat--active" : ""}`}
              onClick={() => setActiveTab("poems")}
            >
              <span className="profile-hero__stat-value">
                {user.poemCount ?? poemsFeed.pagination.totalItems ?? poems.length}
              </span>
              <span className="profile-hero__stat-label">Poems</span>
            </button>
            <button
              type="button"
              className={`profile-hero__stat${activeTab === "followers" ? " profile-hero__stat--active" : ""}`}
              onClick={() => setActiveTab("followers")}
            >
              <span className="profile-hero__stat-value">
                {user.followerCount ?? 0}
              </span>
              <span className="profile-hero__stat-label">Followers</span>
            </button>
            <button
              type="button"
              className={`profile-hero__stat${activeTab === "following" ? " profile-hero__stat--active" : ""}`}
              onClick={() => setActiveTab("following")}
            >
              <span className="profile-hero__stat-value">
                {user.followingCount ?? 0}
              </span>
              <span className="profile-hero__stat-label">Following</span>
            </button>
          </div>
        </div>
      </section>
      {/* Edit modal — portaled so scroll panes cannot clip or overlap it */}
      {isEditing &&
        createPortal(
          <div
            className="profile-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-edit-title"
          >
            <div
              className="profile-edit-modal__backdrop"
              aria-hidden="true"
              onClick={() => {
                if (!isUploading) {
                  setIsEditing(false);
                  setFormErrors({});
                }
              }}
            />
            <div className="profile-edit-modal__card peer-home-page-card">
            <div className="profile-edit-modal__header">
              <div className="profile-edit-modal__header-text">
                <h3 id="profile-edit-title">Edit Profile</h3>
                <p>Update your pen name, bio, and account details.</p>
              </div>
              <button
                type="button"
                className="profile-edit-modal__close"
                aria-label="Close edit profile"
                disabled={isUploading}
                onClick={() => {
                  if (!isUploading) {
                    setIsEditing(false);
                    setFormErrors({});
                  }
                }}
              >
                <FaTimes aria-hidden="true" />
              </button>
            </div>

            <div className="profile-edit-modal__body">
              {formErrors.general && (
                <div className="profile-edit-modal__alert profile-edit-modal__alert--error">
                  <p>{formErrors.general}</p>
                </div>
              )}

              <section className="profile-edit-modal__section" aria-labelledby="profile-edit-about">
                <h4 id="profile-edit-about" className="profile-edit-modal__section-title">
                  About you
                </h4>
                <p className="profile-edit-modal__section-desc">
                  Your pen name is shown on poems and comments. Email is used for login and notifications.
                </p>
                <div className="profile-edit-modal__fields">
                  <div className="profile-edit-modal__field">
                    <label htmlFor="profile-edit-name" className="profile-edit-modal__label">
                      Full name <span className="profile-edit-modal__optional">(optional)</span>
                    </label>
                    <input
                      id="profile-edit-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`peer-home-input profile-edit-modal__input${
                        formErrors.name ? " profile-edit-modal__input--error" : ""
                      }`}
                      placeholder="Your display name"
                    />
                    {formErrors.name && (
                      <p className="profile-edit-modal__field-error">{formErrors.name}</p>
                    )}
                  </div>
                  <div className="profile-edit-modal__field">
                    <label htmlFor="profile-edit-username" className="profile-edit-modal__label">
                      Pen name
                    </label>
                    <input
                      id="profile-edit-username"
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      className={`peer-home-input profile-edit-modal__input${
                        formErrors.username ? " profile-edit-modal__input--error" : ""
                      }`}
                      placeholder="e.g. riverpoet"
                      ref={usernameRef}
                    />
                    {formErrors.username && (
                      <p className="profile-edit-modal__field-error">{formErrors.username}</p>
                    )}
                  </div>
                  <div className="profile-edit-modal__field">
                    <label htmlFor="profile-edit-email" className="profile-edit-modal__label">
                      Email
                    </label>
                    <input
                      id="profile-edit-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`peer-home-input profile-edit-modal__input${
                        formErrors.email ? " profile-edit-modal__input--error" : ""
                      }`}
                      placeholder="you@example.com"
                      ref={emailRef}
                    />
                    {formErrors.email && (
                      <p className="profile-edit-modal__field-error">{formErrors.email}</p>
                    )}
                  </div>
                  <div className="profile-edit-modal__field">
                    <label htmlFor="profile-edit-bio" className="profile-edit-modal__label">
                      Short bio
                    </label>
                    <textarea
                      id="profile-edit-bio"
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      className={`peer-home-input profile-edit-modal__input profile-edit-modal__textarea${
                        formErrors.bio ? " profile-edit-modal__input--error" : ""
                      }`}
                      placeholder="A few words about you or your poetry"
                      rows={4}
                    />
                    {formErrors.bio && (
                      <p className="profile-edit-modal__field-error">{formErrors.bio}</p>
                    )}
                  </div>
                </div>
              </section>
            </div>

            <div className="profile-edit-modal__footer">
              <div className="profile-edit-modal__footer-actions">
                <button
                  type="button"
                  onClick={handleUpdate}
                  disabled={isUploading}
                  className="profile-edit-modal__btn profile-edit-modal__btn--primary"
                >
                  {isUploading ? (
                    <>
                      <span className="profile-edit-modal__spinner profile-edit-modal__spinner--btn" />
                      Saving…
                    </>
                  ) : (
                    "Save changes"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setFormErrors({});
                  }}
                  disabled={isUploading}
                  className="profile-edit-modal__btn profile-edit-modal__btn--secondary"
                >
                  Cancel
                </button>
              </div>
              <div className="profile-edit-modal__danger">
                <p className="profile-edit-modal__danger-title">Danger zone</p>
                <p className="profile-edit-modal__danger-desc">
                  Permanently deletes your account, poems, and followers. This cannot be undone.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteProfileModal({ open: true });
                    setIsEditing(false);
                  }}
                  disabled={isUploading}
                  className="profile-edit-modal__btn profile-edit-modal__btn--danger"
                >
                  Delete profile
                </button>
              </div>
            </div>
          </div>
        </div>,
          document.body
        )}
      {/* Tabs */}
      <div className="profile-tabs-wrap">
      <div className="profile-tabs" ref={profileTabsRef} role="tablist">
        <button
          type="button"
          className={`peer-home-tab${
            activeTab === "poems" ? " peer-home-tab--active" : ""
          }`}
          onClick={() => setActiveTab("poems")}
        >
          Poems
          <span className="profile-tab-count">
            {user.poemCount ?? poemsFeed.pagination.totalItems ?? poems.length}
          </span>
        </button>
        <button
          type="button"
          className={`peer-home-tab${
            activeTab === "drafts" ? " peer-home-tab--active" : ""
          }`}
          onClick={() => setActiveTab("drafts")}
        >
          Drafts
          <span className="profile-tab-count">
            {draftsFeed.pagination.totalItems ?? drafts.length}
          </span>
        </button>
        <button
          type="button"
          className={`peer-home-tab${
            activeTab === "followers" ? " peer-home-tab--active" : ""
          }`}
          onClick={() => setActiveTab("followers")}
        >
          Followers
          <span className="profile-tab-count">
            {user.followerCount ?? followersFeed.pagination.totalItems ?? 0}
          </span>
        </button>
        <button
          type="button"
          className={`peer-home-tab${
            activeTab === "following" ? " peer-home-tab--active" : ""
          }`}
          onClick={() => setActiveTab("following")}
        >
          Following
          <span className="profile-tab-count">
            {user.followingCount ?? followingFeed.pagination.totalItems ?? 0}
          </span>
        </button>
        <button
          type="button"
          className={`peer-home-tab${
            activeTab === "saved" ? " peer-home-tab--active" : ""
          }`}
          onClick={() => setActiveTab("saved")}
        >
          Saved
          <span className="profile-tab-count">
            {savedFeed.pagination.totalItems ?? savedPoems.length}
          </span>
        </button>
      </div>
      </div>
      {/* Tab content */}
      {activeTab === "poems" && (
        <div className="profile-panel">
          {!isLoading && (user?.poemCount > 0 || poems.length > 0) && (
            <div className="profile-panel__toolbar">
              <h3 className="profile-panel__title">Your poems</h3>
              <button
                type="button"
                onClick={() => {
                  setDownloadPoemsModal({ open: true });
                  setSelectedPoems(new Set());
                }}
                className="profile-hero__btn-secondary"
                title="Download poems as PDF"
                aria-label="Download poems"
              >
                <FaDownload className="w-3.5 h-3.5" />
                Download
              </button>
            </div>
          )}
          {isLoading || poemsFeed.loading ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <div key={idx} className="profile-poem-card pointer-events-none mb-3">
                <Skeleton className="h-3 w-16 mb-3" />
                <Skeleton className="h-6 w-1/2 mb-2" />
                <SkeletonText lines={2} />
              </div>
            ))
          ) : poems.length === 0 ? (
            <EmptyState
              icon="poem"
              title="You haven't written any poems yet"
              message="Start sharing your poetry with the world! Write your first poem and let your creativity shine."
              action="write"
              actionLabel="Write Your First Poem"
              actionLink="/write"
              className="py-8"
            />
          ) : (
            <>
              {poems.map((poem, idx) => {
                const excerpt = getPoemPlainText(poem);
                const dated = formatPoemDate(poem.createdAt);
                const isPinned =
                  idx === 0 &&
                  pinnedPoemId &&
                  String(poem._id) === String(pinnedPoemId);
                return (
                  <article
                    key={poem._id}
                    className="profile-poem-card"
                    onClick={() => navigate(`/poems/${poem._id}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/poems/${poem._id}`);
                      }
                    }}
                    role="link"
                    tabIndex={0}
                  >
                    <div className="profile-poem-card__eyebrow">
                      {isPinned ? (
                        <span className="profile-poem-card__pin inline-flex items-center gap-1">
                          <FaThumbtack className="w-3 h-3" /> Pinned
                        </span>
                      ) : (
                        <span>Poem</span>
                      )}
                    </div>
                    <h4 className="profile-poem-card__title">{poem.title}</h4>
                    {excerpt && (
                      <p className="profile-poem-card__excerpt">{excerpt}</p>
                    )}
                    {dated && (
                      <div className="profile-poem-card__meta">{dated}</div>
                    )}
                  </article>
                );
              })}
              <ProfileTabLoadMore
                sentinelRef={poemsFeed.sentinelRef}
                loadingMore={poemsFeed.loadingMore}
                hasNextPage={poemsFeed.pagination.hasNextPage}
                onLoadMore={poemsFeed.loadMore}
                itemCount={poems.length}
              />
            </>
          )}
        </div>
      )}
      {activeTab === "drafts" && (
        <div className="profile-panel">
          {draftsFeed.loading ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <div key={idx} className="profile-draft-card">
                <div className="flex-1 min-w-0">
                  <Skeleton className="h-3 w-16 mb-2" />
                  <Skeleton className="h-5 w-1/2" />
                </div>
              </div>
            ))
          ) : drafts.length === 0 ? (
            <EmptyState
              icon="write"
              title="No drafts yet"
              message="Start writing and save your work as drafts to continue later."
              action="write"
              actionLabel="Write a Poem"
              actionLink="/write"
            />
          ) : (
            <>
            {drafts.map((poem) => (
              <div key={poem._id} className="profile-draft-card">
                <div className="flex-1 min-w-0">
                  <div className="profile-poem-card__eyebrow">Draft</div>
                  <h4 className="profile-poem-card__title">{poem.title || "Untitled draft"}</h4>
                  {formatPoemDate(poem.updatedAt || poem.createdAt) && (
                    <div className="profile-poem-card__meta">
                      Updated {formatPoemDate(poem.updatedAt || poem.createdAt)}
                    </div>
                  )}
                </div>
                <div className="profile-draft-card__actions">
                  <button
                    type="button"
                    onClick={() => navigate(`/edit-poem/${poem._id}`)}
                    className="profile-draft-card__btn profile-draft-card__btn--edit"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteDraftModal({ open: true, poemId: poem._id })}
                    className="profile-draft-card__btn profile-draft-card__btn--delete"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            <ProfileTabLoadMore
              sentinelRef={draftsFeed.sentinelRef}
              loadingMore={draftsFeed.loadingMore}
              hasNextPage={draftsFeed.pagination.hasNextPage}
              onLoadMore={draftsFeed.loadMore}
              itemCount={drafts.length}
            />
            </>
          )}
        </div>
      )}
      {activeTab === "saved" && (
        <div className="profile-panel space-y-4">
          {savedFeed.loading ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <div key={idx} className="profile-poem-card pointer-events-none">
                <Skeleton className="h-3 w-16 mb-3" />
                <Skeleton className="h-5 w-1/2 mb-2" />
                <SkeletonText lines={2} />
              </div>
            ))
          ) : !savedFeed.loading && savedPoems.length === 0 ? (
            <EmptyState
              icon="bookmark"
              title="No saved poems yet"
              message="Save poems to revisit them later."
              className="py-8"
            />
          ) : (
            <>
            {savedPoems.map((poem) => (
              <div key={poem._id}>
                {(() => {
                  const PoemCard = require("../components/PoemCard").default;
                  return (
                    <PoemCard
                      poem={poem}
                      showSave
                      saveAfterThumbnail
                      initialSaved={true}
                      onSavedChange={(nextSaved, p) => {
                        if (!nextSaved) {
                          savedFeed.setItems((prev) => prev.filter((x) => x._id !== p._id));
                        }
                      }}
                    />
                  );
                })()}
              </div>
            ))}
            <ProfileTabLoadMore
              sentinelRef={savedFeed.sentinelRef}
              loadingMore={savedFeed.loadingMore}
              hasNextPage={savedFeed.pagination.hasNextPage}
              onLoadMore={savedFeed.loadMore}
              itemCount={savedPoems.length}
            />
            </>
          )}
        </div>
      )}
      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 sm:p-6">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowSettingsModal(false)}></div>
          <div className="relative peer-home-page-card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-gray-900">Blocked Users</h2>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {loadingBlocked ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#f26522] mx-auto mb-4"></div>
                  <p className="text-sm text-gray-600">Loading blocked users...</p>
                </div>
              ) : blockedUsers.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600 mb-2">You haven't blocked any users.</p>
                  <p className="text-sm text-gray-500">Blocked users will appear here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {blockedUsers.map((blockedUser) => (
                    <div
                      key={blockedUser._id}
                      className="flex items-center justify-between bg-white rounded-xl shadow-sm p-4 border border-[#eeeeee] hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={getAvatarUrl(blockedUser.avatar)}
                          alt="avatar"
                          className="w-10 h-10 rounded-full object-cover border"
                          onError={(e) => {
                            e.currentTarget.src =
                              e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                                ? FALLBACK_AVATAR_SVG
                                : DEFAULT_AVATAR_DATA_URL;
                          }}
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800">{blockedUser.name || blockedUser.username}</div>
                          <div className="text-sm text-gray-500">@{blockedUser.username}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnblock(blockedUser.username)}
                        disabled={unblocking}
                        className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {unblocking ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700"></div>
                        ) : (
                          "Unblock"
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {activeTab === "followers" && (
        <div className="profile-panel py-2">
          {followersFeed.loading ? (
            <div className="profile-people-list">
              {Array.from({ length: 3 }).map((_, idx) => (
                <div key={idx} className="profile-people-card">
                  <SkeletonAvatar size="w-11 h-11" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          ) : followersFeed.items.length > 0 ? (
            <div className="profile-people-list">
              {followersFeed.items.map((follower) => (
                <div key={follower._id} className="profile-people-card">
                  <img
                    src={getAvatarUrl(follower.avatar)}
                    alt=""
                    className="profile-people-card__avatar"
                    onError={(e) => {
                      e.currentTarget.src =
                        e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                          ? FALLBACK_AVATAR_SVG
                          : DEFAULT_AVATAR_DATA_URL;
                    }}
                  />
                  <div className="profile-people-card__info">
                    <div className="profile-people-card__name">
                      {follower.name || follower.username}
                    </div>
                    <button
                      type="button"
                      className="profile-people-card__username"
                      onClick={() => navigate(`/users/${follower.username}`)}
                    >
                      @{follower.username}
                    </button>
                  </div>
                  {loggedInUser && loggedInUser.username !== follower.username && (
                    (user.following || []).some((u) => u.username === follower.username) ? (
                      <button
                        type="button"
                        onClick={() => unfollowUser(follower.username)}
                        className="profile-people-card__action profile-people-card__action--unfollow"
                      >
                        Unfollow
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => followUser(follower.username)}
                        className="profile-people-card__action profile-people-card__action--follow"
                      >
                        Follow
                      </button>
                    )
                  )}
                </div>
              ))}
              <ProfileTabLoadMore
                sentinelRef={followersFeed.sentinelRef}
                loadingMore={followersFeed.loadingMore}
                hasNextPage={followersFeed.pagination.hasNextPage}
                onLoadMore={followersFeed.loadMore}
                itemCount={followersFeed.items.length}
              />
            </div>
          ) : (
            <p className="profile-empty-note">No followers yet.</p>
          )}
        </div>
      )}
      {activeTab === "following" && (
        <div className="profile-panel py-2">
          {followingFeed.loading ? (
            <div className="profile-people-list">
              {Array.from({ length: 3 }).map((_, idx) => (
                <div key={idx} className="profile-people-card">
                  <SkeletonAvatar size="w-11 h-11" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          ) : followingFeed.items.length > 0 ? (
            <div className="profile-people-list">
              {followingFeed.items.map((followed) => (
                <div key={followed._id} className="profile-people-card">
                  <img
                    src={getAvatarUrl(followed.avatar)}
                    alt=""
                    className="profile-people-card__avatar"
                    onError={(e) => {
                      e.currentTarget.src =
                        e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                          ? FALLBACK_AVATAR_SVG
                          : DEFAULT_AVATAR_DATA_URL;
                    }}
                  />
                  <div className="profile-people-card__info">
                    <div className="profile-people-card__name">
                      {followed.name || followed.username}
                    </div>
                    <button
                      type="button"
                      className="profile-people-card__username"
                      onClick={() => navigate(`/users/${followed.username}`)}
                    >
                      @{followed.username}
                    </button>
                  </div>
                  {loggedInUser && loggedInUser.username !== followed.username && (
                    (user.following || []).some((u) => u.username === followed.username) ? (
                      <button
                        type="button"
                        onClick={() => unfollowUser(followed.username)}
                        className="profile-people-card__action profile-people-card__action--unfollow"
                      >
                        Unfollow
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => followUser(followed.username)}
                        className="profile-people-card__action profile-people-card__action--follow"
                      >
                        Follow
                      </button>
                    )
                  )}
                </div>
              ))}
              <ProfileTabLoadMore
                sentinelRef={followingFeed.sentinelRef}
                loadingMore={followingFeed.loadingMore}
                hasNextPage={followingFeed.pagination.hasNextPage}
                onLoadMore={followingFeed.loadMore}
                itemCount={followingFeed.items.length}
              />
            </div>
          ) : (
            <p className="profile-empty-note">Not following anyone yet.</p>
          )}
        </div>
      )}
      {/* moved Blocked into Settings */}
      {showBackToTop && !isEditing && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 peer-home-btn-primary px-4 py-3 sm:px-5 sm:py-3 rounded-full shadow-lg z-50 text-sm sm:text-base"
          aria-label="Back to Top"
        >
          ↑ Back to Top
        </button>
      )}
    </div>
    </PageWithRail>
  );
}

export default Profile;
